import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { motion } from 'framer-motion';
import { Card, CardHeader, CardTitle, CardContent, CardDescription } from '@/components/ui/card';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { 
  Crown, Zap, Star, Calendar, RefreshCw, 
  CreditCard, Ticket, CheckCircle2, ArrowRight,
  User, Mail, Shield, Globe, Trash2, BarChart3, Lock, XCircle
} from 'lucide-react';
import { toast } from 'sonner';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
const API = `${BACKEND_URL}/api`;

const AccountPage = () => {
  const navigate = useNavigate();
  const { token, user, refreshUser } = useAuth();
  const [usage, setUsage] = useState(null);
  const [limits, setLimits] = useState(null);
  const [loading, setLoading] = useState(true);
  const [couponCode, setCouponCode] = useState('');
  const [applyingCoupon, setApplyingCoupon] = useState(false);
  const [domainCount, setDomainCount] = useState(0);

  useEffect(() => {
    fetchAccountData();
  }, [token]);

  const fetchAccountData = async () => {
    if (!token) return;
    setLoading(true);
    try {
      const [usageRes, limitsRes, configsRes] = await Promise.all([
        axios.get(`${API}/subscription/usage`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/subscription/limits`, { headers: { Authorization: `Bearer ${token}` } }),
        axios.get(`${API}/configs`, { headers: { Authorization: `Bearer ${token}` } }).catch(() => ({ data: { domains: [] } }))
      ]);
      setUsage(usageRes.data);
      setLimits(limitsRes.data);
      setDomainCount(configsRes.data.domains?.length || 0);
      if (refreshUser) await refreshUser();
    } catch (error) {
      console.error('Error fetching account data:', error);
    } finally {
      setLoading(false);
    }
  };

  const applyCoupon = async () => {
    if (!couponCode.trim()) {
      toast.error('Please enter a coupon code');
      return;
    }
    
    setApplyingCoupon(true);
    try {
      const response = await axios.post(
        `${API}/coupons/apply`,
        { code: couponCode },
        { headers: { Authorization: `Bearer ${token}` } }
      );
      toast.success(response.data.message);
      setCouponCode('');
      fetchAccountData();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Invalid coupon code');
    } finally {
      setApplyingCoupon(false);
    }
  };

  const getPlanDetails = () => {
    const plan = user?.subscription?.plan || 'free';
    const plans = {
      free: {
        name: 'Free Plan',
        icon: Zap,
        color: 'from-slate-500 to-slate-600',
        bgColor: 'bg-slate-100',
        features: [
          { text: '1 WordPress domain', icon: Globe, included: true },
          { text: '7 days dashboard data', icon: BarChart3, included: true },
          { text: '50 posts fetch limit', icon: CheckCircle2, included: true },
          { text: '3 deletes per day', icon: Trash2, included: true },
          { text: 'Column sorting', icon: Lock, included: false },
          { text: 'Full dashboard data', icon: Lock, included: false },
        ],
        limits: {
          domains: 1,
          dashboardDays: 7,
          postsPerSession: 50,
          deletesPerDay: 3,
          canSort: false
        }
      },
      pro: {
        name: 'Professional',
        icon: Crown,
        color: 'from-purple-500 to-cyan-500',
        bgColor: 'bg-purple-100',
        features: [
          { text: '9 WordPress domains', icon: Globe, included: true },
          { text: 'All dashboard data', icon: BarChart3, included: true },
          { text: 'Unlimited posts fetch', icon: CheckCircle2, included: true },
          { text: '50 deletes per day', icon: Trash2, included: true },
          { text: 'Column sorting', icon: CheckCircle2, included: true },
          { text: 'Priority support', icon: Star, included: true },
        ],
        limits: {
          domains: 9,
          dashboardDays: -1,
          postsPerSession: -1,
          deletesPerDay: 50,
          canSort: true
        }
      },
      lifetime: {
        name: 'Lifetime Deal',
        icon: Star,
        color: 'from-amber-500 to-yellow-500',
        bgColor: 'bg-amber-100',
        features: [
          { text: '9 WordPress domains', icon: Globe, included: true },
          { text: 'All dashboard data', icon: BarChart3, included: true },
          { text: 'Unlimited posts fetch', icon: CheckCircle2, included: true },
          { text: '50 deletes per day', icon: Trash2, included: true },
          { text: 'Column sorting', icon: CheckCircle2, included: true },
          { text: 'Priority support forever', icon: Star, included: true },
          { text: 'Lifetime updates', icon: Zap, included: true },
        ],
        limits: {
          domains: 9,
          dashboardDays: -1,
          postsPerSession: -1,
          deletesPerDay: 50,
          canSort: true
        }
      }
    };
    return plans[plan] || plans.free;
  };

  const planDetails = getPlanDetails();
  const PlanIcon = planDetails.icon;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <RefreshCw className="w-8 h-8 text-purple-600 animate-spin" />
      </div>
    );
  }

  return (
    <div className="max-w-4xl mx-auto space-y-6">
      {/* User Info */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center gap-2">
            <User className="w-5 h-5 text-purple-600" />
            Account Information
          </CardTitle>
        </CardHeader>
        <CardContent>
          <div className="flex items-center gap-4">
            <div className="w-16 h-16 rounded-full bg-gradient-to-br from-purple-500 to-cyan-500 flex items-center justify-center text-white text-2xl font-bold">
              {user?.email?.charAt(0).toUpperCase()}
            </div>
            <div>
              <p className="text-lg font-semibold text-slate-900">{user?.email}</p>
              <p className="text-sm text-slate-500">Member since {user?.created_at ? new Date(user.created_at).toLocaleDateString() : 'N/A'}</p>
            </div>
          </div>
        </CardContent>
      </Card>

      {/* Current Plan */}
      <Card className="overflow-hidden">
        <div className={`h-2 bg-gradient-to-r ${planDetails.color}`} />
        <CardHeader>
          <CardTitle className="flex items-center justify-between">
            <span className="flex items-center gap-2">
              <PlanIcon className="w-5 h-5" />
              Current Plan
            </span>
            <span className={`px-4 py-1 rounded-full text-sm font-bold bg-gradient-to-r ${planDetails.color} text-white`}>
              {planDetails.name}
            </span>
          </CardTitle>
        </CardHeader>
        <CardContent className="space-y-6">
          {/* Features */}
          <div>
            <p className="text-sm font-medium text-slate-700 mb-3">Plan Features:</p>
            <div className="grid grid-cols-1 md:grid-cols-2 gap-2">
              {planDetails.features.map((feature, idx) => {
                const FeatureIcon = feature.icon;
                return (
                  <div key={idx} className={`flex items-center gap-2 text-sm ${feature.included ? 'text-slate-600' : 'text-slate-400'}`}>
                    {feature.included ? (
                      <FeatureIcon className="w-4 h-4 text-green-500" />
                    ) : (
                      <XCircle className="w-4 h-4 text-slate-300" />
                    )}
                    <span className={!feature.included ? 'line-through' : ''}>{feature.text}</span>
                  </div>
                );
              })}
            </div>
          </div>

          {/* Usage Stats */}
          <div className="border-t pt-6">
            <p className="text-sm font-medium text-slate-700 mb-3">Current Usage:</p>
            <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{domainCount}/{planDetails.limits.domains}</p>
                <p className="text-sm text-slate-500">Domains</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">
                  {planDetails.limits.dashboardDays === -1 ? 'All' : `${planDetails.limits.dashboardDays}d`}
                </p>
                <p className="text-sm text-slate-500">Dashboard</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">
                  {planDetails.limits.postsPerSession === -1 ? '∞' : planDetails.limits.postsPerSession}
                </p>
                <p className="text-sm text-slate-500">Posts/Session</p>
              </div>
              <div className="p-4 bg-slate-50 rounded-lg">
                <p className="text-2xl font-bold text-slate-900">{planDetails.limits.deletesPerDay}</p>
                <p className="text-sm text-slate-500">Deletes/Day</p>
              </div>
            </div>
            {usage && (
              <div className="mt-4 p-3 bg-blue-50 rounded-lg">
                <p className="text-sm text-blue-700">
                  <Calendar className="w-4 h-4 inline mr-1" />
                  Today: {usage.refreshes_today || 0} refreshes used
                  {limits?.max_refreshes_per_day !== -1 && ` of ${limits?.max_refreshes_per_day}`}
                </p>
              </div>
            )}
          </div>

          {/* Billing Info for Pro */}
          {user?.subscription?.plan === 'pro' && (
            <div className="border-t pt-6">
              <p className="text-sm font-medium text-slate-700 mb-3">Billing:</p>
              <div className="flex items-center justify-between p-4 bg-purple-50 rounded-lg">
                <div className="flex items-center gap-3">
                  <CreditCard className="w-5 h-5 text-purple-600" />
                  <div>
                    <p className="font-medium text-slate-900">$9.99 / month</p>
                    <p className="text-sm text-slate-500">Recurring monthly payment</p>
                  </div>
                </div>
              </div>
            </div>
          )}

          {/* Lifetime Badge */}
          {user?.subscription?.plan === 'lifetime' && (
            <div className="border-t pt-6">
              <div className="flex items-center gap-3 p-4 bg-gradient-to-r from-amber-50 to-yellow-50 rounded-lg border border-amber-200">
                <Star className="w-6 h-6 text-amber-500" />
                <div>
                  <p className="font-medium text-amber-900">Lifetime Access</p>
                  <p className="text-sm text-amber-700">No recurring payments - you're set for life!</p>
                </div>
              </div>
            </div>
          )}

          {/* Upgrade CTA for Free users */}
          {user?.subscription?.plan === 'free' && (
            <div className="border-t pt-6">
              <Button 
                onClick={() => navigate('/pricing')}
                className="w-full bg-gradient-to-r from-purple-600 to-cyan-600 hover:from-purple-700 hover:to-cyan-700"
              >
                Upgrade to Pro
                <ArrowRight className="w-4 h-4 ml-2" />
              </Button>
            </div>
          )}
        </CardContent>
      </Card>

      {/* Coupon Code Section */}
      {user?.subscription?.plan !== 'lifetime' && (
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center gap-2">
              <Ticket className="w-5 h-5 text-purple-600" />
              Have a Coupon Code?
            </CardTitle>
            <CardDescription>
              Enter your lifetime deal coupon code to unlock permanent access
            </CardDescription>
          </CardHeader>
          <CardContent>
            <div className="flex gap-3">
              <Input
                placeholder="Enter coupon code (e.g., LIFE-XXXX-XXXX)"
                value={couponCode}
                onChange={(e) => setCouponCode(e.target.value.toUpperCase())}
                className="flex-1 font-mono"
              />
              <Button 
                onClick={applyCoupon} 
                disabled={applyingCoupon}
                className="bg-gradient-to-r from-amber-500 to-yellow-500 hover:from-amber-600 hover:to-yellow-600 text-black font-semibold"
              >
                {applyingCoupon ? <RefreshCw className="w-4 h-4 animate-spin" /> : 'Apply'}
              </Button>
            </div>
          </CardContent>
        </Card>
      )}
    </div>
  );
};

export default AccountPage;
